import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { FROM_SEARCH } from '../shell/useBack'
import { api } from '../../api/endpoints'
import { errorMessage } from '../../api/client'
import type { SearchHit } from '../../api/types'
import { Icon } from '../../components/Icon'
import { db, type LocalChannel } from '../../db/db'
import { useDebounced } from '../../hooks/useDebounced'
import { useUserMap } from '../../hooks/useUsers'
import { channelDisplayName } from '../../lib/channels'
import { useConnection } from '../../state/connection'
import { useSession } from '../../state/session'
import { upsertUsers } from '../../sync/applyEvents'
import { ScreenHeader } from '../shell/ScreenHeader'
import { FilterPage } from '../../components/FilterPage'
import { TimeFilterPage } from '../../components/TimeFilterPage'
import { dateToIso, timeLabel } from '../../lib/timeRanges'
import { localSnippet, searchLocalMessages } from './localSearch'
import { clearRecentSearches, loadRecentSearches, rememberSearch } from './recentSearches'
import { SearchResultGroups, type ResultItem } from './SearchResultGroups'

const NO_CHANNELS: LocalChannel[] = []

export function SearchScreen() {
  const navigate = useNavigate()
  const me = useSession((s) => s.me)!
  const online = useConnection((s) => s.online)
  const users = useUserMap()
  const [params, setParams] = useSearchParams()
  const [q, setQ] = useState(params.get('q') ?? '')
  const [channelId, setChannelId] = useState(params.get('channelId') ?? '')
  const [authorId, setAuthorId] = useState(params.get('authorId') ?? '')
  const [from, setFrom] = useState(params.get('from') ?? '')
  const [to, setTo] = useState(params.get('to') ?? '')
  const channels = useLiveQuery(() => db.channels.toArray(), [], NO_CHANNELS)
  // Which filter's page is open.
  const [picking, setPicking] = useState<'channel' | 'author' | 'time' | null>(null)
  const [recent, setRecent] = useState(() => loadRecentSearches(me.id))
  const remember = (query: string) => setRecent(rememberSearch(me.id, query))
  const channelMap = useMemo(() => new Map(channels.map((c) => [c.id, c])), [channels])

  const filters = useMemo(
    () => ({ q: q.trim(), channelId: channelId || undefined, authorId: authorId || undefined, from: dateToIso(from), to: dateToIso(to, true) }),
    [q, channelId, authorId, from, to],
  )
  const debounced = useDebounced(filters, 350)
  const key = JSON.stringify(debounced)

  const [local, setLocal] = useState<ResultItem[]>([])
  const [server, setServer] = useState<{ key: string; hits: SearchHit[]; nextCursor: string | null } | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Keep the URL shareable and restorable.
  useEffect(() => {
    const next = new URLSearchParams()
    if (debounced.q) next.set('q', debounced.q)
    if (channelId) next.set('channelId', channelId)
    if (authorId) next.set('authorId', authorId)
    if (from) next.set('from', from)
    if (to) next.set('to', to)
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirror the debounced query only
  }, [key])

  // Instant results from this device.
  useEffect(() => {
    let cancelled = false
    if (!filters.q) {
      setLocal([])
      return
    }
    void searchLocalMessages(filters.q, filters).then((messages) => {
      if (!cancelled) setLocal(messages.map((m) => ({ channelId: m.channelId, message: m, snippet: localSnippet(m.body || m.attachments[0]?.fileName || '', filters.q) })))
    })
    return () => {
      cancelled = true
    }
  }, [filters])

  // Ranked server results.
  useEffect(() => {
    if (!debounced.q || !online) {
      setServer(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .search(debounced)
      .then(async (res) => {
        await upsertUsers(res.users)
        if (!cancelled) setServer({ key, hits: res.results, nextCursor: res.nextCursor })
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the serialised filters
  }, [key, online])

  const loadMore = async () => {
    if (!server?.nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const res = await api.search({ ...debounced, cursor: server.nextCursor })
      await upsertUsers(res.users)
      setServer((s) => (s && s.key === key ? { key, hits: [...s.hits, ...res.results], nextCursor: res.nextCursor } : s))
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setLoadingMore(false)
    }
  }

  // More results as the end comes into view, as a phone's lists do; a browser that can't tell keeps the button.
  const scrolls = typeof IntersectionObserver !== 'undefined'
  const sentinel = useRef<HTMLDivElement>(null)
  const loadMoreRef = useRef(loadMore)
  useEffect(() => {
    loadMoreRef.current = loadMore
  })
  const nextCursor = server?.key === key ? server.nextCursor : null
  useEffect(() => {
    const el = sentinel.current
    if (!el || !scrolls || !nextCursor) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) void loadMoreRef.current()
    }, { rootMargin: '300px' })
    observer.observe(el)
    return () => observer.disconnect()
  }, [scrolls, nextCursor])

  const open = (item: ResultItem) => {
    remember(debounced.q)
    navigate(`/c/${item.channelId}?m=${encodeURIComponent(item.message.id)}&seq=${item.message.seq}`, { state: FROM_SEARCH })
  }
  const serverItems: ResultItem[] = server && server.key === key ? server.hits.map((h) => ({ channelId: h.channelId, message: h.message, snippet: h.snippet })) : []
  const showServer = Boolean(server && server.key === key)
  // People and bots. A managing system's actor (kind "integration") never writes messages, so it is not offered.
  const authors = useMemo(
    () => [...users.values()].filter((u) => u.kind !== 'integration').sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [users],
  )
  const authorOptions = useMemo(
    () =>
      authors.map((u) => ({ id: u.id, label: u.id === me.id ? 'You' : u.kind === 'bot' ? `${u.displayName} (bot)` : u.displayName })),
    [authors, me.id],
  )
  const channelOptions = useMemo(
    () => channels.map((c) => ({ id: c.id, name: channelDisplayName(c, users, me.id) })).sort((a, b) => a.name.localeCompare(b.name)),
    [channels, users, me.id],
  )

  return (
    <div className="screen">
      <ScreenHeader title="Search" subtitle="Find messages across your conversations" />
      <div className="screen-body">
        <form
          className="search-form"
          role="search"
          onSubmit={(e) => {
            e.preventDefault()
            remember(q)
          }}
        >
          <div className="search-field">
            <Icon name="search" />
            <input type="search" placeholder="Search messages" aria-label="Search messages" value={q} onChange={(e) => setQ(e.target.value)} autoFocus autoComplete="off" />
          </div>
          {/* Where, who and when, as chips: each opens its own page, instead of four form fields over the results. */}
          <div className="filter-chips" role="group" aria-label="Filters">
            <button type="button" className="filter-chip" aria-pressed={Boolean(channelId)} onClick={() => setPicking('channel')}>
              {channelOptions.find((c) => c.id === channelId)?.name ?? 'All conversations'}
              <Icon name="chevronDown" />
            </button>
            <button type="button" className="filter-chip" aria-pressed={Boolean(authorId)} onClick={() => setPicking('author')}>
              {authorOptions.find((a) => a.id === authorId)?.label ?? 'Anyone'}
              <Icon name="chevronDown" />
            </button>
            <button type="button" className="filter-chip" aria-pressed={Boolean(from || to)} onClick={() => setPicking('time')}>
              {timeLabel(from, to)}
              <Icon name="chevronDown" />
            </button>
          </div>
        </form>
        {picking === 'channel' ? (
          <FilterPage
            title="Search in"
            searchLabel="Find a conversation"
            options={[{ id: '', label: 'All conversations' }, ...channelOptions.map((c) => ({ id: c.id, label: c.name }))]}
            chosen={channelId}
            onPick={(id) => {
              setChannelId(id)
              setPicking(null)
            }}
            onClose={() => setPicking(null)}
          />
        ) : null}
        {picking === 'author' ? (
          <FilterPage
            title="Written by"
            searchLabel="Find someone"
            options={[{ id: '', label: 'Anyone' }, ...authorOptions]}
            chosen={authorId}
            onPick={(id) => {
              setAuthorId(id)
              setPicking(null)
            }}
            onClose={() => setPicking(null)}
          />
        ) : null}
        {picking === 'time' ? (
          <TimeFilterPage
            title="Messages from"
            from={from}
            to={to}
            onPick={(nextFrom, nextTo) => {
              setFrom(nextFrom)
              setTo(nextTo)
              setPicking(null)
            }}
            onClose={() => setPicking(null)}
          />
        ) : null}

        {!filters.q ? (
          <>
            {recent.length ? (
              <section className="recent-searches" aria-labelledby="recent-heading">
                <div className="recent-searches-head">
                  <h3 id="recent-heading">Recent</h3>
                  <button
                    type="button"
                    className="text-button"
                    aria-label="Clear recent searches"
                    onClick={() => {
                      clearRecentSearches(me.id)
                      setRecent([])
                    }}
                  >
                    Clear
                  </button>
                </div>
                <ul aria-label="Recent searches">
                  {recent.map((term) => (
                    <li key={term}>
                      <button type="button" className="recent-search" onClick={() => setQ(term)}>
                        <Icon name="search" />
                        <span>{term}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : (
              <div className="empty-state">
                <Icon name="search" />
                <strong>Search your messages</strong>
              </div>
            )}
            <p className="search-tip">Use quotes for phrases and a minus sign to leave words out, e.g. "delivery note" -invoice.</p>
          </>
        ) : null}

        {filters.q && (!showServer || !online || error) ? (
          <>
            <div className="results-label">
              On this device {loading ? <span className="spinner" aria-label="Searching the server" /> : null}
            </div>
            {local.length ? (
              <SearchResultGroups items={local} channels={channelMap} users={users} meId={me.id} onOpen={open} />
            ) : (
              <p className="notice">{loading ? 'Searching…' : 'No matches stored on this device.'}</p>
            )}
            {!online ? <p className="notice" style={{ marginTop: 12 }}>You're offline. Showing messages stored on this device.</p> : null}
            {error ? (
              <p className="notice error" role="alert" style={{ marginTop: 12 }}>
                {error}
              </p>
            ) : null}
          </>
        ) : null}

        {filters.q && showServer && online && !error ? (
          <>
            <div className="results-label">{serverItems.length ? 'Results' : 'No results'}</div>
            {serverItems.length ? (
              <SearchResultGroups items={serverItems} channels={channelMap} users={users} meId={me.id} onOpen={open} />
            ) : (
              <p className="notice">Nothing matched. Try fewer words or remove a filter.</p>
            )}
            {server?.nextCursor ? (
              scrolls ? (
                <div ref={sentinel} className="load-more">
                  {loadingMore ? <span className="spinner" aria-label="Loading more results" /> : null}
                </div>
              ) : (
                <div className="load-more">
                  <button type="button" className="button" disabled={loadingMore} onClick={() => void loadMore()}>
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}
